import { lazy, type ComponentType } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';

import { AuthProvider } from '@/hooks/useAuth';
import { ThemeProvider } from '@/components/common/ThemeProvider';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
import { MainLayout } from '@/components/common/Layout/MainLayout';
import { LoginPage } from '@/pages/Auth/LoginPage';

/**
 * Страницы загружаются по требованию: первый экран легче, остальной код
 * подтягивается при переходе в раздел (заглушка — PageLoader в MainLayout).
 */
const page = <M extends Record<string, unknown>>(loader: () => Promise<M>, name: keyof M) =>
  lazy(() => loader().then((module) => ({ default: module[name] as ComponentType })));

const DashboardPage = page(() => import('@/pages/Dashboard/DashboardPage'), 'DashboardPage');
const ProductsListPage = page(() => import('@/pages/Products/ProductsListPage'), 'ProductsListPage');
const CategoriesPage = page(() => import('@/pages/Categories/CategoriesPage'), 'CategoriesPage');
const WarehousePage = page(() => import('@/pages/Warehouse/WarehousePage'), 'WarehousePage');
const SuppliersPage = page(() => import('@/pages/Suppliers/SuppliersPage'), 'SuppliersPage');
const DocumentsPage = page(() => import('@/pages/Documents/DocumentsPage'), 'DocumentsPage');
const DocumentsJournalPage = page(() => import('@/pages/Journal/DocumentsJournalPage'), 'DocumentsJournalPage');
const ReceiptPage = page(() => import('@/pages/Documents/ReceiptPage'), 'ReceiptPage');
const ReceiptScanPage = page(() => import('@/pages/Documents/ReceiptScanPage'), 'ReceiptScanPage');
const WriteoffPage = page(() => import('@/pages/Documents/WriteoffPage'), 'WriteoffPage');
const StockBalancesPage = page(() => import('@/pages/StockBalances/StockBalancesPage'), 'StockBalancesPage');
const StockMovementsPage = page(() => import('@/pages/StockMovements/StockMovementsPage'), 'StockMovementsPage');
const CreateTransferPage = page(() => import('@/pages/StockMovements/CreateTransferPage'), 'CreateTransferPage');
const RecipesListPage = page(() => import('@/pages/Recipes/RecipesListPage'), 'default');
const MenuListPage = page(() => import('@/pages/Menu/MenuListPage'), 'default');
const MenuEditPage = page(() => import('@/pages/Menu/MenuEditPage'), 'default');
const MenuDisplayPage = page(() => import('@/pages/Menu/MenuDisplayPage'), 'default');
const MenuCategoriesPage = page(() => import('@/pages/Menu/MenuCategoriesPage'), 'default');
const MenuItemsPage = page(() => import('@/pages/Menu/MenuItemsPage'), 'default');
const WarehouseMenuPage = page(() => import('@/pages/Menu/WarehouseMenuPage'), 'default');
const InventoryList = page(() => import('@/pages/Inventory/InventoryList'), 'default');
const CreateInventory = page(() => import('@/pages/Inventory/CreateInventory'), 'default');
const InventoryDetail = page(() => import('@/pages/Inventory/InventoryDetail'), 'default');
const InventoryAnalysis = page(() => import('@/pages/Inventory/InventoryAnalysis'), 'default');
const SalesPage = page(() => import('@/pages/Sales/SalesPage'), 'SalesPage');
const SalesDashboard = page(() => import('@/pages/Sales/SalesDashboard'), 'SalesDashboard');
const SalesHistoryPage = page(() => import('@/pages/Sales/SalesHistoryPage'), 'SalesHistoryPage');
const ProductionReleaseListPage = page(
  () => import('@/pages/ProductionRelease/ProductionReleaseListPage'),
  'ProductionReleaseListPage'
);
const ProductionReleaseFormPage = page(
  () => import('@/pages/ProductionRelease/ProductionReleaseFormPage'),
  'ProductionReleaseFormPage'
);
const ProductionReleaseDetailPage = page(
  () => import('@/pages/ProductionRelease/ProductionReleaseDetailPage'),
  'ProductionReleaseDetailPage'
);
const CounterpartiesPage = page(() => import('@/pages/Counterparties/CounterpartiesPage'), 'CounterpartiesPage');
const ContractsListPage = page(() => import('@/pages/Contracts/ContractsListPage'), 'ContractsListPage');
const ContractFormPage = page(() => import('@/pages/Contracts/ContractFormPage'), 'ContractFormPage');
const LaundryOrdersPage = page(() => import('@/pages/Laundry/LaundryOrdersPage'), 'LaundryOrdersPage');
const LaundryOrderFormPage = page(() => import('@/pages/Laundry/LaundryOrderFormPage'), 'LaundryOrderFormPage');
const LaundryOrderDetailPage = page(() => import('@/pages/Laundry/LaundryOrderDetailPage'), 'LaundryOrderDetailPage');
const LaundryStatementPage = page(() => import('@/pages/Laundry/LaundryStatementPage'), 'LaundryStatementPage');
const LaundrySettingsPage = page(() => import('@/pages/Laundry/LaundrySettingsPage'), 'LaundrySettingsPage');
const MealSystemPage = page(() => import('@/pages/MealSystem/MealSystemPage'), 'MealSystemPage');
const ReceiptRulesPage = page(() => import('@/pages/ReceiptRules/ReceiptRulesPage'), 'ReceiptRulesPage');
const ReportsPage = page(() => import('@/pages/Reports/ReportsPage'), 'default');
const SettingsPage = page(() => import('@/pages/Settings/SettingsPage'), 'SettingsPage');
const NotFoundPage = page(() => import('@/pages/NotFound/NotFoundPage'), 'NotFoundPage');

// Создание клиента React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 минут
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              {/* Публичные маршруты */}
              <Route path="/login" element={<LoginPage />} />

              {/* Защищенные маршруты */}
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <MainLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />

                {/* Номенклатура */}
                <Route path="products" element={<ProductsListPage />} />
                <Route path="categories" element={<CategoriesPage />} />
                <Route path="recipes" element={<RecipesListPage />} />

                {/* Меню */}
                <Route path="menu" element={<MenuListPage />} />
                <Route path="menu/:id/edit" element={<MenuEditPage />} />
                <Route path="menu/display" element={<MenuDisplayPage />} />
                <Route path="menu/categories" element={<MenuCategoriesPage />} />
                <Route path="menu/items" element={<MenuItemsPage />} />
                <Route path="menu/warehouse-settings" element={<WarehouseMenuPage />} />

                <Route path="warehouse" element={<WarehousePage />} />
                <Route path="suppliers" element={<SuppliersPage />} />
                <Route path="documents" element={<DocumentsPage />} />
                <Route path="journal" element={<DocumentsJournalPage />} />
                <Route path="documents/receipt" element={<ReceiptPage />} />
                <Route path="documents/receipt/scan" element={<ReceiptScanPage />} />
                <Route path="documents/writeoff" element={<WriteoffPage />} />
                <Route path="stock-balances" element={<StockBalancesPage />} />
                <Route path="stock-movements" element={<StockMovementsPage />} />
                <Route path="stock-movements/create" element={<CreateTransferPage />} />
                <Route path="inventory" element={<InventoryList />} />
                <Route path="inventory/create" element={<CreateInventory />} />
                <Route path="inventory/:id" element={<InventoryDetail />} />
                <Route path="inventory/:id/analysis" element={<InventoryAnalysis />} />

                {/* Выпуск продукции */}
                <Route path="production" element={<ProductionReleaseListPage />} />
                <Route path="production/new" element={<ProductionReleaseFormPage />} />
                <Route path="production/:id" element={<ProductionReleaseDetailPage />} />
                <Route path="production/:id/edit" element={<ProductionReleaseFormPage />} />
                <Route path="counterparties" element={<CounterpartiesPage />} />

                {/* Договоры */}
                <Route path="contracts" element={<ContractsListPage />} />
                <Route path="contracts/new" element={<ContractFormPage />} />
                <Route path="contracts/:id/edit" element={<ContractFormPage />} />

                {/* Продажи */}
                <Route path="sales" element={<SalesPage />} />
                <Route path="sales/dashboard" element={<SalesDashboard />} />
                <Route path="sales/history" element={<SalesHistoryPage />} />

                {/* Прачечная */}
                <Route path="laundry" element={<LaundryOrdersPage />} />
                <Route path="laundry/new" element={<LaundryOrderFormPage />} />
                <Route path="laundry/statement" element={<LaundryStatementPage />} />
                <Route path="laundry/settings" element={<LaundrySettingsPage />} />
                <Route path="laundry/:id" element={<LaundryOrderDetailPage />} />
                <Route path="laundry/:id/edit" element={<LaundryOrderFormPage />} />

                <Route path="meal-system" element={<MealSystemPage />} />
                <Route path="receipt-rules" element={<ReceiptRulesPage />} />

                {/* Отчеты */}
                <Route path="reports" element={<ReportsPage />} />

                {/* Настройки — администратор; менеджеру — только журнал аудита (сервер тоже проверяет роль) */}
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute requiredRole={['ADMIN', 'MANAGER']}>
                      <SettingsPage />
                    </ProtectedRoute>
                  }
                />

                {/* Несуществующий адрес — понятная страница вместо молчаливого перехода на главную */}
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </Router>
        </AuthProvider>

        {/* Уведомления */}
        <Toaster
          position="bottom-right"
          gutter={10}
          containerStyle={{ inset: 20 }}
          toastOptions={{
            duration: 4000,
            className: 'text-[13px] font-medium',
            style: {
              background: 'hsl(var(--popover))',
              color: 'hsl(var(--popover-foreground))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '10px',
              boxShadow: '0 12px 32px -8px rgb(15 23 42 / 0.18), 0 4px 8px -4px rgb(15 23 42 / 0.08)',
              padding: '10px 14px',
              maxWidth: 420,
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: 'hsl(var(--success))',
                secondary: 'hsl(var(--success-foreground))',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: 'hsl(var(--destructive))',
                secondary: 'hsl(var(--destructive-foreground))',
              },
            },
          }}
        />
      </ThemeProvider>

      {/* React Query DevTools */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default App;
